import React, { createContext, useContext, useCallback } from 'react'
import { Check, WarningIcon, InfoIcon } from '@phosphor-icons/react'
import { toast } from 'sonner'

export type NotificationType = 'success' | 'error' | 'info' | 'warning'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message?: string
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

interface NotificationContextValue {
  notifications: Notification[]
  addNotification: (notification: Omit<Notification, 'id'>) => string
  removeNotification: (id: string) => void
  clearAll: () => void
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined)

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return context
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9)

    const Icon = getNotificationIcon(notification.type)
    
    const handleActionClick = () => {
      notification.action?.onClick()
      toast.dismiss(id)
    }
    
    // Use Sonner toast with custom styling based on type
    const toastFunction = notification.type === 'error' ? toast.error
      : notification.type === 'success' ? toast.success
      : notification.type === 'warning' ? toast.warning
      : toast.info

    toastFunction(
      <div className="flex items-start gap-3 w-full">
        <div className="shrink-0 mt-0.5">
          <Icon size={20} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">
            {notification.title}
          </div>
          {notification.message && (
            <div className="text-sm mt-1 opacity-90">
              {notification.message}
            </div>
          )}
          {notification.action && (
            <div className="flex justify-end mt-3">
              <button
                onClick={handleActionClick}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {notification.action.label}
              </button>
            </div>
          )}
        </div>
      </div>,
      {
        id,
        duration: notification.duration ?? 5000,
      }
    )

    return id
  }, [])

  const removeNotification = useCallback((id: string) => {
    toast.dismiss(id)
  }, [])

  const clearAll = useCallback(() => {
    toast.dismiss()
  }, [])

  return (
    <NotificationContext.Provider
      value={{ 
        notifications: [], // Not used anymore since Sonner manages internally
        addNotification, 
        removeNotification, 
        clearAll 
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'success':
      return Check
    case 'error':
      return WarningIcon
    case 'info':
      return InfoIcon
    case 'warning':
      return WarningIcon
  }
}

